import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';

import { NS } from '@/constants';
import { URLS } from '@/constants/urls';
import { useAppDispatch, useAppSelector } from '@/redux';
import { isObjectEmpty } from '@/utils/comparisonUtils';

import { toggleDeliveryScheduleModal } from '@/redux/slices/gui.slice';

import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';
import Icon from '@/components/common/iconedLinks/Icon';

import styles from './DeliverySchedule.module.scss';

type DeliveryScheduleProps = {
  shippingId: string;
  isExproCheckout: boolean;
  isDayOnly: boolean;
}

export const DeliverySchedule = ({ shippingId, isExproCheckout, isDayOnly }: DeliveryScheduleProps): React.JSX.Element => {
  const { t } = useTranslation(NS.CHECKOUT);
  const dispatch = useAppDispatch();
  const locale = useAppSelector(({ root }) => root.storeConf.locale);

  const deliverySlotData = useAppSelector(({ cart }) => cart.deliverySlotData);
  const deliverySlotDataExpro = useAppSelector(({ expro }) => expro.deliverySlotDataExpro);
  const bookingSlots = useAppSelector(({ cart }) => cart.content.shippingMode?.bookingSlot);

  const [bookingSlot, setBookingSlot] = useState(isExproCheckout ? deliverySlotDataExpro : deliverySlotData);

  useEffect(() => {
    if (!isExproCheckout) {
      setBookingSlot(deliverySlotData);
    }

    if (isObjectEmpty(deliverySlotData) && !isObjectEmpty(bookingSlots)) {
      const date = new Date(bookingSlots.start);
      const formattedDate = new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(date);

      const formattedStartTime = new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit'
      }).format(new Date(bookingSlots?.start));

      const formattedEndTime = new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit'
      }).format(new Date(bookingSlots?.end));

      const formatedForFrontBookingSlot = {
        day: formattedDate,
        slot: {
          start: formattedStartTime,
          end: formattedEndTime
        }
      };
      setBookingSlot(formatedForFrontBookingSlot);
    }
  }, [deliverySlotData]);

  useEffect(() => {
    if (isExproCheckout) {
      setBookingSlot(deliverySlotDataExpro);
    }
  }, [deliverySlotDataExpro]);

  return (
    <div className={styles.container}>
      {!isObjectEmpty(bookingSlot) && (
        <div className={styles.text} data-test-id="delivery-date">
          <Icon src={URLS.CHECKBOX_CONFIRM_CIRCLE_GREEN} alt="confirmed" />
          <span>{t('shipMode.topChrono.delivery')}</span>
          <span className={styles.day}>
            {bookingSlot.day.replaceAll('-', '/')} {bookingSlot.slot?.start} - {bookingSlot.slot?.end}
          </span>
        </div>
      )}
      <CallToActionContainer
        onClick={() => dispatch(toggleDeliveryScheduleModal({ opened: true, shippingId }))}
      >
        {!isObjectEmpty(bookingSlot) ?
          (isDayOnly ?
            t('shipMode.topChrono.changeDate') :
            t('shipMode.topChrono.changeSlot')) :
          (isDayOnly ?
            t('shipMode.topChrono.pickDate') :
            t('shipMode.topChrono.pickDateAndSlot'))
        }
      </CallToActionContainer>
    </div>
  );
};

export default DeliverySchedule;
